import axios from 'axios';
import { t } from 'i18next';
import { ArrayValues } from 'type-fest';

import {
  ErrorResponse,
  ErrorResponseErrorItem,
  ErrorResponseErrorItemParameter,
} from '../../../../typings/api/skymap/rest/v1/.common';
import { publish, SubscriptionTopic } from '../../viewer/messaging/pubsub';
import { ErrorResponseErrorItemCode, translations } from './translations';

export interface TranslatedError<T> {
  path: T;
  errorMessage: string;
  originalError?: ErrorResponseErrorItem;
}

type BaseCode =
  | 'conflict'
  | 'resource_not_found'
  | 'unauthenticated'
  | 'unauthorized'
  | 'validation_failed';

export const errorUtils = {
  /**
   * Returns an ErrorResponse object if error is of that type, otherwise undefined.
   * @param error Error from axios.
   */
  toApiErrorResponse: (error: unknown): ErrorResponse | undefined =>
    axios.isAxiosError(error) && error?.response?.data?.errors
      ? (error.response.data as ErrorResponse)
      : undefined,
  isResponseError: (
    error: unknown,
    baseCode: BaseCode,
    errorCode: ErrorResponseErrorItemCode,
    callback?: (errorItem: ErrorResponseErrorItem) => void,
  ): boolean => {
    const apiError = errorUtils.toApiErrorResponse(error);

    if (apiError) {
      const errorItem = findErrorItem(apiError, baseCode, errorCode);
      if (errorItem) {
        callback?.(errorItem);
        return true;
      }
    }

    return false;
  },
};

/**
 * Returns a list of translated errors.
 * @param response API error response.
 */
export const translateApiErrors = <T>(response: ErrorResponse): TranslatedError<T>[] =>
  response.errors.map((error) => ({
    path: buildPathFromErrorSource(error),
    errorMessage: translateError(error),
    originalError: error,
  }));

/**
 * Returns a string representation of source 'in' and 'name'.
 * @param error API error item.
 */
const buildPathFromErrorSource = <T>(error: ErrorResponseErrorItem): T =>
  error.source.name ? (`${error.source.in}.${error.source.name}` as T) : (error.source.in as T);

/**
 * Translates the error code into a swedish text.
 * @param error API error item.
 */
const translateError = (error: ErrorResponseErrorItem): string => {
  const unexpectedError = t('unexpectedError', { ns: 'common' });
  const errorMessage = translations()[error.code];
  return errorMessage ? insertParameters(errorMessage, error.parameters) : unexpectedError;
};

/**
 * Returns a new string with placeholders for parameters replaced by the parameter values.
 * @param errorMessage Error message with parameter placeholders.
 * @param parameters List of parameters (if any) to insert into specified error message.
 */
const insertParameters = (errorMessage: string, parameters?: ErrorResponseErrorItemParameter[]) => {
  if (parameters) {
    for (const param of parameters) {
      errorMessage = errorMessage.replace(`{${param.name}}`, param.value.toString());
    }
  }

  return errorMessage;
};

/**
 * Returns http status code in specified error if found, otherwise undefined.
 * @param error Error catched in exception.
 */
export const getHttpStatusCode = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

/**
 * Handles error and calls specified state function with translated errors for those
 * paths specified. Errors for other paths are shown in toast.
 * @param error Error catched in exception.
 * @param paths List of paths to set errors for using the specified function 'func'.
 * @param funcErrors Function that is called with translated errors for specified paths
 * @param funOtherError Function that is called with translated errors for other paths, or non-related API errors.
 */
export const handleError = <T>(
  error: unknown,
  paths: T[],
  funcErrors:
    | React.Dispatch<React.SetStateAction<TranslatedError<T>[]>>
    | ((errors: TranslatedError<T>[]) => void),
  funcOtherErrors?:
    | React.Dispatch<React.SetStateAction<TranslatedError<T>[]>>
    | ((errors: TranslatedError<T>[]) => void),
): void => {
  const apiError = errorUtils.toApiErrorResponse(error);
  if (apiError) {
    const errors = translateApiErrors<T>(apiError);

    // Set error messages using state function passed in.
    // Include specified paths only.
    funcErrors(errors.filter((x) => paths.length === 0 || paths.includes(x.path)));

    const otherErrors = errors.filter((x) => paths && !paths.includes(x.path));
    if (otherErrors.length > 0) {
      funcOtherErrors?.(otherErrors);

      // Toast error messages for all other paths.
      // todo: Maybe should be grouped into one notification in future.
      if (!funcOtherErrors) {
        for (const error of otherErrors) {
          publish(SubscriptionTopic.ToastrMessage, {
            type: 'error',
            message: error.errorMessage,
          });
        }
      }
    } else {
      funcOtherErrors?.([]);
    }
  } else {
    funcOtherErrors?.([]);

    // If error is not an API error, assume internet connection is down.
    // todo: Check existance of status code and display other message if status code exists.
    publish(SubscriptionTopic.ToastrMessage, {
      type: 'error',
      message: t('networkErrorCheckInternet', {
        ns: 'common',
        defaultValue: 'Kontrollera din internetanslutning. Kontakta vår support om felet kvarstår.',
      }),
    });
  }
};

const findErrorItem = (
  apiError: ErrorResponse,
  baseCode: BaseCode,
  errorCode: ErrorResponseErrorItemCode,
) => {
  return apiError.code === baseCode ? apiError.errors.find((x) => x.code === errorCode) : undefined;
};

export const getResponseErrorParameters = (
  error: unknown,
  baseCode: BaseCode,
  errorCode: ErrorResponseErrorItemCode,
): ErrorResponseErrorItem['parameters'] | undefined => {
  const apiError = errorUtils.toApiErrorResponse(error);
  return apiError ? findErrorItem(apiError, baseCode, errorCode)?.parameters : undefined;
};

export const getResponseErrorParameter = (
  errorItem: ErrorResponseErrorItem,
  parameterName: string,
) => errorItem.parameters?.find((x) => x.name === parameterName);

/**
 * Returns the parameter value given the specified parameter name and error item.
 * If parameter is not found, undefined is returned.
 */
export const getResponseErrorParameterValue = <
  TType extends ArrayValues<NonNullable<ErrorResponseErrorItem['parameters']>>['value'],
>(
  errorItem: ErrorResponseErrorItem,
  parameterName: string,
): TType | undefined => {
  const value = getResponseErrorParameter(errorItem, parameterName)?.value;
  return value ? (value as TType) : undefined;
};

/**
 * Gets any translated error for specified path.
 * @param errorMessages List of error messages.
 * @param path Path to get error for.
 */
export const getErrorForPath = <T>(
  errorMessages: TranslatedError<T>[],
  path: T,
): string | undefined => errorMessages.find((x) => x.path === path)?.errorMessage;

/**
 * Clears error for specified path, using the passed in state and state function.
 * @param errorMessages List of error messages.
 * @param func State function to use to update list of errors.
 * @param path Path to clear error for.
 */
export const clearErrorForPath = <T>(
  errorMessages: TranslatedError<T>[],
  func: React.Dispatch<React.SetStateAction<TranslatedError<T>[]>>,
  path: string,
) => func(errorMessages.filter((x) => x.path !== path));
